/**
 * Workspace API Funktionen
 * 
 * Migration: Custom Instructions → Custom Presets
 * - Neue API: /api/workspace/custom-presets
 * - Legacy API: /api/workspace/custom-instructions (deprecated)
 */

import { apiClient } from '../api-client';
import type {
  ImagesResponse,
  WorkspaceConfigResponse,
  FavoritesResponse,
  AssetsResponse,
  CustomPreset,
  CustomPresetsResponse,
} from '@/types';

// ===========================================
// WORKSPACE CONFIG
// ===========================================

/**
 * Workspace-Konfiguration laden
 */
export async function getWorkspaceConfig(): Promise<WorkspaceConfigResponse> {
  const response = await apiClient.get('/api/workspace/config');
  return response.data;
}

// ===========================================
// IMAGES
// ===========================================

/**
 * Workspace-Bilder mit Pagination laden
 */
export async function getWorkspaceImages(
  page: number = 1,
  perPage: number = 12
): Promise<ImagesResponse> {
  const response = await apiClient.get('/api/workspace/images', {
    params: { page, per_page: perPage },
  });
  return response.data;
}

// ===========================================
// FAVORITES
// ===========================================

/**
 * Favoriten laden
 */
export async function getWorkspaceFavorites(): Promise<FavoritesResponse> {
  const response = await apiClient.get('/api/workspace/favorites');
  return response.data;
}

/**
 * Favoriten speichern
 */
export async function saveWorkspaceFavorites(favorites: string[]): Promise<{ success: boolean }> {
  const response = await apiClient.post('/api/workspace/favorites', { favorites });
  return response.data;
}

// ===========================================
// ASSETS
// ===========================================

/**
 * Assets für Asset Tray laden
 */
export async function getWorkspaceAssets(limit: number = 6): Promise<AssetsResponse> {
  const response = await apiClient.get('/api/workspace/assets', {
    params: { limit },
  });
  return response.data;
}

// ===========================================
// CUSTOM PRESETS (NEU)
// ===========================================

/**
 * Custom Presets laden (neu)
 */
export async function getWorkspaceCustomPresets(): Promise<CustomPresetsResponse> {
  const response = await apiClient.get('/api/workspace/custom-presets');
  return response.data;
}

/**
 * Custom Instructions laden (legacy)
 * @deprecated Use getWorkspaceCustomPresets instead
 */
export async function getWorkspaceCustomInstructions(): Promise<CustomPresetsResponse> {
  const response = await apiClient.get('/api/workspace/custom-instructions');
  return response.data;
}

/**
 * Smart Wrapper: Custom Presets laden
 * 
 * Nutzt neue API, fällt auf Legacy zurück bei Fehler.
 * Normalisiert die Response für konsistente Nutzung.
 */
export async function getWorkspacePresets(): Promise<{
  success: boolean;
  presets: CustomPreset[];
  canUse: boolean;
}> {
  try {
    // Versuche neue API
    const response = await getWorkspaceCustomPresets();
    return {
      success: response.success,
      presets: response.custom_presets || [],
      canUse: response.can_use_custom_presets || response.can_use || false,
    };
  } catch {
    // Fallback auf Legacy API
    try {
      const legacy = await getWorkspaceCustomInstructions();
      return {
        success: legacy.success,
        presets: legacy.custom_presets || legacy.custom_instructions || [],
        canUse: legacy.can_use_custom_presets || legacy.can_use_custom_instructions || legacy.can_use || false,
      };
    } catch {
      return {
        success: false,
        presets: [],
        canUse: false,
      };
    }
  }
}
